import SquareGrid from '@/components/SquareGrid'
import { CBox, RBox } from '@/pages/__styled'
import { Note, PatternDraft, PatternMeshColor } from '@/types'
import { NoteType } from '@/utils/constants'
import { getAssetThumb, isNumeric, isUniqueColor } from '@/utils/helper'
import { Box, styled, Typography } from '@mui/material'

interface PatternNotesGroupListProps {
  title: string
  patternNoteType: NoteType
  data: Note[] // sectioned data, notes inside groups
  patternDetails?: PatternDraft
}

const useStyles = () => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
  },

  noteItemDiv: {
    width: '52px',
    height: '52px',
    borderRadius: '10px',
    borderWidth: 1,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
  },

  colorItemDiv: {
    width: '42px',
    height: '42px',
    borderRadius: '10px',
    borderWidth: 1,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
})

const CardContainer = styled(Box)({
  '& .title': {
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'capitalize',
    color: 'rgba(255, 255, 255, 0.9)',
    whiteSpace: 'nowrap',
    lineHeight: '18px',
    letterSpacing: '-0.01em',
  },
  '& .titleBold': {
    fontWeight: 700,
    fontSize: '15px',
    textTransform: 'capitalize',
    color: '#ffffff',
    whiteSpace: 'nowrap',
    lineHeight: '20px',
    letterSpacing: '-0.02em',
  },
})

const PatternNotesGroupRowList = ({
  title,
  patternNoteType,
  data,
  patternDetails,
}: PatternNotesGroupListProps) => {
  const styles = useStyles()
  let imagePaths: Set<string> = new Set()
  let colorSwatches: PatternMeshColor[][] = []
  let borderNoteText: any = ''

  if (patternNoteType == NoteType.BOARDER) {
    borderNoteText = data[0]?.noteText ? JSON.parse(data[0].noteText) : null
  }

  if (patternNoteType == NoteType.TEMPLATE) {
    imagePaths.add(getAssetThumb(NoteType.TEMPLATE, patternDetails?.templateID || 0) || '')
  } else if (patternNoteType == NoteType.MATERIAL) {
    Object.values(patternDetails?.materialID || []).forEach((e) => {
      if (e) {
        let path = getAssetThumb(NoteType.MATERIAL, e || 0)
        if (path) {
          imagePaths.add(path)
        }
      }
    })
  }
  else if (patternNoteType == NoteType.FRAME && isNumeric(patternDetails?.frameID)) {
    const assetPath = getAssetThumb(NoteType.FRAME, patternDetails?.frameID || 0) || '';
    
    imagePaths.add(getAssetThumb(NoteType.FRAME, patternDetails?.frameID || 0) || '');
  }

  else if (patternNoteType == NoteType.PATTERNFRAME && isNumeric(patternDetails?.patternFrameID)) {
    imagePaths.add(getAssetThumb(NoteType.PATTERNFRAME, patternDetails?.patternFrameID || 0) || '');
  }
  if (patternNoteType == NoteType.ACCENT && isNumeric(patternDetails?.accentID)) {
    imagePaths.add(getAssetThumb(NoteType.ACCENT, patternDetails?.accentID || 0) || '')
  } else if (patternNoteType == NoteType.PRINT) {
    Object.values(patternDetails?.print || [])
      .map((e) => e?.id)
      .forEach((x) => {
        if (x) {
          let path = getAssetThumb(NoteType.PRINT, x)
          if (path) {
            imagePaths.add(path)
          }
        }
      })
  } else if (patternNoteType == NoteType.COLOR) {
    Object.values(patternDetails?.color || {}).forEach((e) => {
      if (Array.isArray(e) && e.length > 0) {
        if (isUniqueColor(colorSwatches, e)) {
          colorSwatches.push(e)
        }
      }
    })
  } else if (patternNoteType == NoteType.FRAME) {
    imagePaths.add(getAssetThumb(NoteType.FRAME, patternDetails?.frameID || 0) || '')
  }

  return (
    <CardContainer>
      {colorSwatches.length > 0 ||
      imagePaths.size > 0 ||
      (patternNoteType == NoteType.BOARDER && borderNoteText && borderNoteText?.width != 0) ? (
        <Box
          sx={{
            background: 'rgba(0, 0, 0, 0.5)',
            padding: '16px 20px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography className='title' sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: '10px' }}>
            {title}
          </Typography>
          { //data?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              {patternNoteType == NoteType.BOARDER ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '12px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography className='titleBold'>{borderNoteText.width * 100}</Typography>
                    <Typography className='title' sx={{ fontSize: '11px' }}>
                      {'Border Size'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography className='titleBold'>{borderNoteText.type}</Typography>
                    <Typography className='title' sx={{ fontSize: '11px' }}>
                      {'Border Type'}
                    </Typography>
                  </Box>
                </Box>
              ) : patternNoteType == NoteType.COLOR ? (
                <RBox gap={'10px'}>
                  {colorSwatches.map((e, i) => (
                    <Box
                      key={`swatch-${i}`}
                      sx={{
                        padding: '0px',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
                          borderColor: 'rgba(99, 102, 241, 0.4)',
                        },
                      }}
                    >
                      <SquareGrid itemSize={18} gap={2}>
                        {e.map((rgb, rgbIndex) => (
                          <Box
                            key={`color-${i}-${rgbIndex}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '4px',
                              backgroundColor: `rgb(${rgb.r * 255},${rgb.g * 255},${rgb.b * 255})`,
                              border: '1px solid rgba(0, 0, 0, 0.15)',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                            }}
                          />
                        ))}
                      </SquareGrid>
                    </Box>
                  ))}
                </RBox>
              ) : (
                <>
                  {Array.from(imagePaths).map((e, i) => (
                    <Box
                      sx={{
                        ...styles.noteItemDiv,
                        backgroundColor:
                          patternNoteType == NoteType.TEMPLATE || patternNoteType == NoteType.ACCENT
                            ? 'rgba(99, 102, 241, 0.15)'
                            : 'rgba(255, 255, 255, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(99, 102, 241, 0.25)',
                          borderColor: 'rgba(99, 102, 241, 0.4)',
                        },
                      }}
                      key={i}
                    >
                      <img
                        style={{
                          width: '100%',
                          height: '100%',
                          cursor: 'default',
                          objectFit: 'cover',
                          borderRadius: '10px',
                        }}
                        src={e}
                        alt='image' draggable="false"
                      />
                    </Box>
                  ))}
                </>
              )}
            </Box>
          //)
          }
        </Box>
      ) : null}
    </CardContainer>
  )
}

export default PatternNotesGroupRowList
